import { usePresence } from 'motion/react'
import React, { useContext, useEffect, useLayoutEffect } from 'react'
import { useLatest } from 'react-use'
import styled from 'styled-components'
import { Drawer as VaulDrawer } from 'vaul'

import { useBackButton } from '@blade/ui-kit'

import { useRootElement } from '../root-element-context'

import { DrawerContent } from './drawer-content'
import { DrawerContext } from './drawer-provider'

import type { Intent } from '@blade/ui-kit'
import type { ClassNameProps } from '@root/shared/react'

// https://github.com/emilkowalski/vaul/blob/73d06cdd3fd990bf4b83214cfe240c246908af0d/src/constants.ts#L2
const DRAWER_TRANSITION_DURATION = 0.5 * 1000

export type DrawerControlProps = {
  open: boolean
  onOpenChange?: (open: boolean) => void
}
export type DrawerProps = {
  title?: React.ReactNode
  transparent?: boolean
  backgroundGradient?: Intent | 'primary' | undefined
} & DrawerControlProps &
  React.PropsWithChildren &
  ClassNameProps &
  React.ComponentProps<typeof VaulDrawer.Root>

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onOpenChange,
  children,
  className,
  title,
  dismissible,
  snapPoints,
  modal = true,
  transparent,
  backgroundGradient,
  ...drawerProps
}) => {
  const onOpenChangeRef = useLatest(onOpenChange)
  const presence = usePresence()
  const [isPresent, safeToRemove] = presence
  const openRef = useLatest(open)
  const dialogsContainer = useRootElement()
  const { withCloseButton } = useContext(DrawerContext)

  useBackButton(
    () => {
      onOpenChangeRef.current?.(false)
    },
    { enabled: open && dismissible !== false },
  )

  useEffect(() => {
    // AnimatePresence signaling that drawer was remove from react tree
    if (!isPresent) {
      // Drawer was open, but was remove from react tree
      if (openRef.current) {
        const timeoutId = setTimeout(() => {
          safeToRemove()
        }, DRAWER_TRANSITION_DURATION)
        return () => {
          clearTimeout(timeoutId)
        }
      }
      safeToRemove()
    }
  }, [isPresent, openRef, safeToRemove])

  // When drawer is used inside AnimatePresence, open is always true
  const drawerOpen = isPresent === false ? false : open

  useEffect(() => {
    if (drawerOpen || !modal) {
      // SEE: https://github.com/emilkowalski/vaul/issues/534
      const rafId = window.requestAnimationFrame(() => {
        document.body.style.pointerEvents = 'auto'
      })
      return () => {
        window.cancelAnimationFrame(rafId)
      }
    }
  }, [drawerOpen, modal])

  useLayoutEffect(() => {
    if (!(drawerOpen || !modal)) {
      return
    }

    // SEE: https://github.com/emilkowalski/vaul/issues/497#issuecomment-2452503724
    const stopFocusPropagation = (e: FocusEvent) => {
      e.stopImmediatePropagation()
    }

    document.addEventListener('focusin', stopFocusPropagation, {
      passive: true,
    })
    document.addEventListener('focusout', stopFocusPropagation, {
      passive: true,
    })

    return () => {
      document.removeEventListener('focusin', stopFocusPropagation)
      document.removeEventListener('focusout', stopFocusPropagation)
    }
  }, [drawerOpen, modal])

  const isClosable = withCloseButton && !snapPoints

  return (
    <VaulDrawer.Root
      fixed
      modal={modal}
      open={drawerOpen}
      onOpenChange={onOpenChange}
      dismissible={dismissible}
      repositionInputs={false}
      container={dialogsContainer}
      {...({ ...drawerProps, snapPoints: snapPoints } as React.ComponentProps<
        typeof VaulDrawer.Root
      >)}
    >
      <VaulDrawer.Portal>
        <DialogOverlay data-ignore-panel-swipe />
        <DrawerContent
          transparent={transparent}
          onClose={isClosable ? () => onOpenChange?.(false) : undefined}
          snapPoints={!!snapPoints}
          className={className}
          title={title}
          backgroundGradient={backgroundGradient}
        >
          {children}
        </DrawerContent>
      </VaulDrawer.Portal>
    </VaulDrawer.Root>
  )
}

const DialogOverlay = styled(VaulDrawer.Overlay)`
  background-color: rgba(0 0 0 / 70%);
  position: fixed;
  inset: 0;
`
